import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X, ChevronRight, ChevronLeft,
  ShoppingBag, MapPin, UserPlus, Headphones,
  Smartphone, KeyRound, LogIn,
} from 'lucide-react'

// Bumped to _v2 so visitors who dismissed the previous tour see the new
// "Moved over from our old platform?" first step (legacy-migration login).
const TOUR_DISMISSED_KEY = 'silux_welcome_tour_dismissed_v2'

interface SubLink { label: string; path: string }
interface TourStep {
  icon: React.ElementType
  title: string
  description: string
  cta?: { label: string; path: string }
  ctas?: { label: string; path: string }[]
  subLinks?: SubLink[]
  steps?: string[]
  color: string
}

const tourSteps: TourStep[] = [
  {
    icon: LogIn,
    title: 'Moved Over From Our Old Platform?',
    description: 'If you were a customer on our previous platform, set up your account on the new one here — we just need you to confirm your details and a mobile number.',
    cta: { label: 'Moved over from our old platform? Login Here', path: '/first-time-login' },
    color: 'from-amber-500 to-amber-600',
  },
  {
    icon: Smartphone,
    title: 'Browse Our Plans',
    description: 'Choose a plan type below to preview it — the tour stays open so you can keep exploring:',
    subLinks: [
      { label: 'A. SIM Only', path: '/sim-only' },
      { label: 'B. Broadband', path: '/broadband' },
      // Roaming is parked (content being re-added later) — link removed so the tour
      // doesn't steer visitors to a page that isn't ready. Re-add when roaming returns.
    ],
    color: 'from-primary-500 to-primary-600',
  },
  {
    icon: ShoppingBag,
    title: 'Explore the Products We Sell',
    description: 'Browse our full catalogue — SIM cards, broadband packages, bundles and more. Filter by network, price, or data allowance.',
    cta: { label: 'Visit the Shop', path: '/shop' },
    color: 'from-violet-500 to-violet-600',
  },
  {
    icon: MapPin,
    title: 'Check Your Coverage',
    description: 'Enter your postcode to instantly check 4G & 5G coverage from every major UK network before you buy.',
    cta: { label: 'Check Coverage', path: '/network-coverage' },
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: UserPlus,
    title: 'Create Your Account',
    description: 'Register for free to track orders, manage SIMs, view invoices, and access your full account dashboard from any device.',
    cta: { label: 'Register Free', path: '/register' },
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    icon: KeyRound,
    title: 'First Time Logging In Guide',
    description: "Moved over from our old platform? Here's how to set up your account:",
    steps: [
      'Check your inbox for "Welcome to Silux Mobile\'s New Platform" — we sent it when we migrated your account.',
      'Click "Set Up My Account" in that email (link valid 30 days; a reminder is re-sent each week until you act).',
      'Set a new password and confirm your mobile number.',
      'Done — from now on log in with your email, new password and the SMS code we send.',
    ],
    ctas: [
      { label: "I didn't get the email — resend it", path: '/first-time-login' },
      { label: 'Go to Log In', path: '/login' },
    ],
    color: 'from-amber-500 to-amber-600',
  },
  {
    icon: Headphones,
    title: "We're Here to Help",
    description: 'Got questions? Browse our FAQ, check network status, or raise a support ticket. Our UK-based team is always ready.',
    cta: { label: 'Visit FAQ', path: '/faq' },
    color: 'from-orange-500 to-orange-600',
  },
]

export function WelcomeTour() {
  const [visible, setVisible] = useState(false)
  const [phase, setPhase] = useState<'welcome' | 'tour'>('welcome')
  const [step, setStep] = useState(0)
  const [highlightActive, setHighlightActive] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (localStorage.getItem(TOUR_DISMISSED_KEY)) return

    // Strictly wait until the home page has fully loaded before opening, so the
    // popup never flashes over a still-loading screen. We listen for window 'load'
    // (all resources painted), then a short beat, then show it — once.
    let delay: ReturnType<typeof setTimeout>
    const open = () => {
      delay = setTimeout(() => setVisible(true), 800)
    }
    if (document.readyState === 'complete') {
      open()
    } else {
      window.addEventListener('load', open, { once: true })
    }
    return () => {
      clearTimeout(delay)
      window.removeEventListener('load', open)
    }
  }, [])

  // Push the sticky navbar down when the tour bar is visible
  useEffect(() => {
    if (phase === 'tour' && visible) {
      document.body.classList.add('silux-tour-active')
    } else {
      document.body.classList.remove('silux-tour-active')
    }
    return () => document.body.classList.remove('silux-tour-active')
  }, [phase, visible])

  const dismiss = () => {
    localStorage.setItem(TOUR_DISMISSED_KEY, 'true')
    document.body.classList.remove('silux-tour-active')
    setVisible(false)
  }

  const startTour = () => setPhase('tour')

  // Navigate to a page but keep the tour open
  const goTo = (path: string) => {
    navigate(path)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    // Brief pulse on the top heading indicator
    setHighlightActive(true)
    setTimeout(() => setHighlightActive(false), 2000)
  }

  const next = () => {
    if (step < tourSteps.length - 1) setStep(step + 1)
    else dismiss()
  }

  const prev = () => {
    if (step > 0) setStep(step - 1)
  }

  if (!visible) return null

  const current = tourSteps[step]
  const Icon = current.icon
  const isLast = step === tourSteps.length - 1

  return (
    <>
      {/* ── Tour mode: top banner ── */}
      {phase === 'tour' && (
        <div
          className={`fixed top-0 left-0 right-0 z-[70] flex items-center justify-between px-4 py-1.5 text-white text-xs font-medium shadow-md transition-colors duration-500 bg-gradient-to-r ${
            highlightActive ? 'from-primary-700 to-primary-500 scale-y-110' : 'from-primary-600 to-primary-500'
          }`}
        >
          <span className="flex items-center gap-2">
            <span className="opacity-70">🗺 Tour Mode</span>
            <span className="opacity-40">·</span>
            <span>Step {step + 1} of {tourSteps.length}:</span>
            <strong>{current.title}</strong>
            {highlightActive && (
              <span className="ml-1 animate-pulse">↑ Heading highlighted above</span>
            )}
          </span>
          <button
            onClick={dismiss}
            className="opacity-70 hover:opacity-100 transition-opacity flex items-center gap-1"
          >
            End Tour <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Welcome screen: centred modal ── */}
      {phase === 'welcome' && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={dismiss}
        >
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={dismiss}
              className="absolute top-3 right-3 z-10 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center mb-5">
                <img src="/silux_logo.png" alt="Silux Mobile" className="h-16 w-auto" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Silux Mobile</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                New here? Let us walk you through what we offer — it only takes a minute.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={startTour}
                  className="flex-1 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/40 hover:-translate-y-0.5 transition-all"
                >
                  Start Here →
                </button>
                <button
                  onClick={dismiss}
                  className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-all"
                >
                  Skip Tour
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tour steps: floating corner widget ── */}
      {phase === 'tour' && (
        <div className="fixed bottom-4 left-4 z-[60] w-80 bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 z-10 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-all"
            aria-label="Close tour"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Coloured header strip */}
          <div className={`bg-gradient-to-r ${current.color} px-4 py-4 flex items-center gap-3`}>
            <div className="p-2.5 bg-white/20 rounded-xl flex-shrink-0">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white/70 text-xs font-medium uppercase tracking-wide">
                Step {step + 1} of {tourSteps.length}
              </p>
              <h3 className="text-white text-sm font-bold leading-tight">{current.title}</h3>
            </div>
          </div>

          {/* Body */}
          <div className="p-4">
            <p className="text-gray-600 text-xs leading-relaxed mb-3">{current.description}</p>

            {/* Numbered guide (e.g. First Time Logging In) */}
            {current.steps && (
              <ol className="text-gray-700 text-xs leading-relaxed mb-4 space-y-1.5 list-decimal list-outside pl-5">
                {current.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            )}

            {/* Sub-links (e.g. Plans step) */}
            {current.subLinks && (
              <div className="space-y-2 mb-4">
                {current.subLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => goTo(link.path)}
                    className={`w-full text-left px-3 py-2 rounded-xl bg-gradient-to-r ${current.color} text-white text-xs font-semibold hover:shadow-md hover:-translate-y-0.5 transition-all`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}

            {/* Single CTA */}
            {current.cta && (
              <button
                onClick={() => goTo(current.cta!.path)}
                className={`w-full text-center py-2 rounded-xl text-white font-semibold text-xs bg-gradient-to-r ${current.color} hover:shadow-md hover:-translate-y-0.5 transition-all mb-4`}
              >
                {current.cta.label}
              </button>
            )}

            {/* Multi-CTA (e.g. First Time Logging In) */}
            {current.ctas && (
              <div className="space-y-2 mb-4">
                {current.ctas.map((c) => (
                  <button
                    key={c.path}
                    onClick={() => goTo(c.path)}
                    className={`w-full text-center py-2 rounded-xl text-white font-semibold text-xs bg-gradient-to-r ${current.color} hover:shadow-md hover:-translate-y-0.5 transition-all`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mb-3">
              {tourSteps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`rounded-full transition-all ${
                    i === step ? 'w-5 h-2 bg-primary-600' : 'w-2 h-2 bg-gray-200 hover:bg-gray-300'
                  }`}
                  aria-label={`Go to step ${i + 1}`}
                />
              ))}
            </div>

            {/* Prev / Next navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                disabled={step === 0}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="Previous step"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                onClick={next}
                className={`flex-1 py-1.5 rounded-lg text-white font-semibold text-xs bg-gradient-to-r ${current.color} hover:shadow-sm transition-all flex items-center justify-center gap-1`}
              >
                {isLast ? 'Finish Tour' : 'Next'}
                {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shift navbar down while tour bar is visible, restore on end */}
      {phase === 'tour' && (
        <style>{`
          .silux-tour-active header.sticky,
          .silux-tour-active header[class*="sticky"] {
            top: 30px !important;
          }
        `}</style>
      )}

      {/* Highlight ring on page h1 when tour navigates — injected via style tag */}
      {phase === 'tour' && highlightActive && (
        <style>{`
          body > * h1:first-of-type,
          main h1:first-of-type {
            outline: 3px solid #38A146 !important;
            outline-offset: 6px !important;
            border-radius: 6px;
            animation: tourHeadingPulse 1s ease-in-out;
          }
          @keyframes tourHeadingPulse {
            0%   { outline-color: #38A146; box-shadow: 0 0 0 0 rgba(56,161,70,0.5); }
            50%  { outline-color: #38A146; box-shadow: 0 0 0 12px rgba(56,161,70,0); }
            100% { outline-color: #38A146; box-shadow: 0 0 0 0 rgba(56,161,70,0); }
          }
        `}</style>
      )}
    </>
  )
}
